/**
 * Tap snapping. Pure logic — no MapLibre, no DOM — so the behaviour that makes
 * small countries playable can be tested without a GPU.
 *
 * Two hit sources feed it:
 *
 *  - POLYGONS. Probed by point sampling, because that is all
 *    `queryRenderedFeatures` offers: it answers "what is under this pixel",
 *    never "how far away".
 *  - POINT TARGETS. Countries that are smaller than a fingertip at the current
 *    zoom — the 29 that Natural Earth 110m drops entirely, plus every country
 *    whose rendered footprint is under `SNAP.subResolutionPx`. Their anchors
 *    are projected, so their screen distance is measured exactly rather than
 *    probed for. See `targets.ts` for why that set is dynamic.
 *
 * The search walks outward in rings so the FIRST polygon hit is also the
 * nearest one, and stops once no polygon could still be closer than the best
 * anchor.
 *
 * AMBIGUITY. Distance alone cannot answer a tap in the Lesser Antilles: at the
 * default view St Lucia and St Vincent are 1.8 px apart, so "nearest anchor"
 * is a lottery among seven countries, and no bias, radius or tie-break can
 * recover information the projection threw away. `snapResolve` says so instead
 * of guessing — it returns the contenders, and the caller magnifies. Reporting
 * the tie leaks nothing: the contenders are neighbours of the FINGER, not of
 * the answer, and the caller never learns which one the round wants.
 */

export interface RingOffset {
  readonly dx: number;
  readonly dy: number;
  /** Ring radius in CSS pixels. Offsets are emitted in ascending order. */
  readonly r: number;
}

export interface PointCandidate {
  readonly cca3: string;
  /** Straight-line screen distance from the tap to the target's anchor. */
  readonly distancePx: number;
  /**
   * The target's own rendered radius in CSS pixels — 0 for a country with no
   * polygon at all. It is SUBTRACTED from the bias, so a country that already
   * has a visible shape reaches less far past it: nothing claims more than
   * `microBiasPx` from its anchor however small it is. Without that, Lesotho
   * (a 3.5px enclave inside South Africa at the default view) would claim a
   * 12px disc on top of its own polygon and swallow the middle of its host.
   */
  readonly radiusPx?: number;
  /**
   * False for the countries 110m has no polygon for at all. Those are the only
   * targets allowed to OUTRANK the polygon under the finger, because tapping
   * their own shape is not an option. A small country that does have a polygon
   * makes the tap ambiguous instead of stealing it — see `snapResolve`.
   */
  readonly hasGeometry?: boolean;
}

export interface SnapProbes {
  /** Country whose polygon covers this screen pixel, or null. */
  polygonAt(x: number, y: number): string | null;
  /** Every point target within the snap radius, in any order. */
  pointCandidates(): readonly PointCandidate[];
  /**
   * Is this country currently picked as a point rather than a polygon?
   * Defaults to false, which reproduces the pre-existing behaviour exactly.
   */
  isPointTarget?(cca3: string): boolean;
}

export interface SnapOptions {
  readonly ringStepPx?: number;
  readonly arcStepPx?: number;
  /** Distance credit given to point candidates. See `theme.SNAP.microBiasPx`. */
  readonly microBiasPx?: number;
  /** Distance gap below which two point candidates are a coin flip. */
  readonly aimTolerancePx?: number;
  /**
   * How close a tap must land to a geometry-less country's dot before it may
   * outrank the polygon under the finger outright rather than reporting a tie.
   */
  readonly pointCertaintyPx?: number;
}

export interface SnapResolution {
  /** ISO alpha-3 the tap resolved to, or null for open ocean. */
  readonly cca3: string | null;
  /**
   * Point targets the tap could equally well have meant, nearest first, when
   * the winner is one of them and the runners-up are inside the aim tolerance.
   * Two or more means the pick is a guess and the caller should magnify rather
   * than commit. Empty whenever the pick is decisive.
   */
  readonly contenders: readonly string[];
}

const DEFAULT_BIAS_PX = 12;
const DEFAULT_AIM_TOLERANCE_PX = 8;
const DEFAULT_CERTAINTY_PX = 6;

/**
 * How far past its own footprint a point target may claim a tap. A country
 * with no polygon claims the full bias; one already `radiusPx` wide claims
 * only the remainder, so the total tappable disc is `microBiasPx` for all of
 * them and no small country over-reaches into its host.
 */
function reachOf(c: PointCandidate, biasPx: number): number {
  return Math.max(0, biasPx - (c.radiusPx ?? 0));
}

/**
 * Sample offsets for concentric rings out to `maxRadiusPx`, ascending.
 *
 * Samples per ring scale with circumference so the arc gap stays near
 * `arcStepPx` at every radius. A constant sample count would leave 40px holes
 * on the outer ring — exactly where the small countries you are reaching for
 * tend to be.
 */
export function ringOffsets(maxRadiusPx: number, opts: SnapOptions = {}): RingOffset[] {
  const ringStep = Math.max(1, opts.ringStepPx ?? 4);
  const arcStep = Math.max(1, opts.arcStepPx ?? 5);
  const out: RingOffset[] = [];

  for (let r = ringStep; r <= maxRadiusPx + 1e-6; r += ringStep) {
    const count = Math.max(6, Math.min(64, Math.ceil((2 * Math.PI * r) / arcStep)));
    // Rotate every other ring by half a step so samples interleave radially.
    const phase = ((r / ringStep) % 2) * (Math.PI / count);
    for (let i = 0; i < count; i++) {
      const a = phase + (i * 2 * Math.PI) / count;
      out.push({ dx: Math.cos(a) * r, dy: Math.sin(a) * r, r });
    }
  }

  return out;
}

/**
 * Resolve one tap to a country, plus the contenders when it is a coin flip.
 *
 * Resolution order:
 *  1. A tap inside a sub-resolution country's OWN polygon is decisive — you
 *     cannot hit a six-pixel country by accident, so you meant it. The one
 *     exception is a nearer point target inside the reach, which is how
 *     Liechtenstein still beats Austria when Austria itself is sub-resolution
 *     at minimum zoom.
 *  2. A geometry-less target inside its reach beats the polygon under the
 *     finger — Vatican over Italy, Monaco over France — because there is no
 *     shape of its own to tap instead.
 *  3. A small country that DOES have a polygon, inside its reach, with the
 *     finger in a different country: ambiguous. Report both and let the caller
 *     magnify. Stealing the tap here is how "tap the middle of Senegal" used
 *     to answer Guinea-Bissau.
 *  4. Otherwise the polygon under the tap, or an outward ring search over
 *     polygons, abandoned as soon as the ring radius exceeds the best point's
 *     credited distance.
 */
export function snapResolve(
  x: number,
  y: number,
  snapRadiusPx: number,
  probes: SnapProbes,
  opts: SnapOptions = {},
): SnapResolution {
  const bias = opts.microBiasPx ?? DEFAULT_BIAS_PX;
  const aim = opts.aimTolerancePx ?? DEFAULT_AIM_TOLERANCE_PX;
  const certainty = opts.pointCertaintyPx ?? DEFAULT_CERTAINTY_PX;

  // Credited distance: real screen distance minus the target's reach. It is
  // deliberately allowed to go negative, so two anchors a few pixels apart
  // still order correctly instead of both flattening to zero.
  const near = probes
    .pointCandidates()
    .filter((c) => c.distancePx <= snapRadiusPx)
    .map((c) => ({
      cca3: c.cca3,
      distancePx: c.distancePx,
      credited: c.distancePx - reachOf(c, bias),
      hasGeometry: c.hasGeometry !== false,
    }))
    // Stable alphabetical tie-break, so a dead heat between two anchors at
    // identical distance does not depend on iteration order.
    .sort((a, b) => a.credited - b.credited || (a.cca3 < b.cca3 ? -1 : 1));

  const best = near[0] ?? null;
  const bestCredited = best ? best.credited : Number.POSITIVE_INFINITY;
  const pointWins = best !== null && bestCredited < 0;

  const direct = probes.polygonAt(x, y);
  const bestHasGeometry = best !== null && best.hasGeometry;

  let winner: string | null = null;

  if (direct !== null) {
    // A tap inside a sub-resolution country's OWN polygon is decisive: you
    // cannot hit a six-pixel country by accident, so you meant it. Unless a
    // nearer point target wins, which is how Liechtenstein still beats Austria
    // at minimum zoom, where Austria is itself sub-resolution.
    if (
      probes.isPointTarget?.(direct) === true &&
      (best === null || best.cca3 === direct || !pointWins)
    ) {
      // ...unless another point target is close enough that the tap could
      // equally well have been aimed at it. Grenada and Trinidad are four
      // pixels apart at the default view, so landing on Trinidad's polygon is
      // not evidence you meant Trinidad. `-bias` is the credit a dead-centre
      // hit earns, which is what being inside the shape is worth.
      const rivals = near.filter((c) => c.cca3 !== direct && c.credited < aim - bias);
      if (rivals.length > 0) {
        return { cca3: direct, contenders: [direct, ...rivals.map((c) => c.cca3)] };
      }
      return { cca3: direct, contenders: [] };
    }

    if (pointWins && best !== null && best.cca3 !== direct) {
      // A geometry-less country has no shape to tap, so proximity to its dot
      // is the only evidence there is — and within `certaintyPx` (the drawn
      // dot plus a fingertip) it is good evidence. Vatican over Italy, Monaco
      // over France.
      if (!bestHasGeometry && best.distancePx <= certainty) {
        winner = best.cca3;
      } else {
        // Otherwise the finger is inside someone else's polygon and only NEAR
        // the small country. Both readings are plausible — Andorra's window
        // reaches the middle of Spain, Guinea-Bissau's the middle of Senegal —
        // so this is a coin flip, not a steal. The caller magnifies and asks
        // again rather than silently answering the wrong country.
        return { cca3: direct, contenders: [best.cca3, direct] };
      }
    } else {
      winner = direct;
    }
  } else {
    // Open water. Walk outward until a polygon turns up, or until no polygon
    // could still be closer than the best anchor. The bound is the anchor's
    // RAW distance, not its credited one: a point target's reach is a claim on
    // taps aimed at it, not a licence to beat a coastline two pixels away —
    // that is how a tap in the Irish Sea off Blackpool used to answer Ireland.
    let hitCode: string | null = null;
    let hitR = 0;
    if (snapRadiusPx > 0) {
      for (const o of ringOffsets(snapRadiusPx, opts)) {
        if (best !== null && o.r > best.distancePx) break;
        const found = probes.polygonAt(x + o.dx, y + o.dy);
        if (found !== null) {
          hitCode = found;
          hitR = o.r;
          break;
        }
      }
    }

    if (hitCode === null || best === null) {
      winner = hitCode ?? best?.cca3 ?? null;
    } else if (bestCredited >= hitR || best.cca3 === hitCode) {
      // The polygon is at least as good, or it IS the best anchor's own shape.
      winner = best.cca3 === hitCode ? best.cca3 : hitCode;
    } else if (!bestHasGeometry && best.distancePx <= certainty) {
      winner = best.cca3;
    } else {
      return { cca3: hitCode, contenders: [best.cca3, hitCode] };
    }
  }

  if (winner === null || best === null || winner !== best.cca3) {
    return { cca3: winner, contenders: [] };
  }

  const contenders = near.filter((c) => c.credited - best.credited < aim).map((c) => c.cca3);
  return { cca3: winner, contenders: contenders.length > 1 ? contenders : [] };
}

/** {@link snapResolve} without the ambiguity report. */
export function snapPick(
  x: number,
  y: number,
  snapRadiusPx: number,
  probes: SnapProbes,
  opts: SnapOptions = {},
): string | null {
  return snapResolve(x, y, snapRadiusPx, probes, opts).cca3;
}
